import { useState } from 'react';
import { toast } from 'sonner';

import { Modal } from '@/components/Modal';
import { CapabilitySchedule } from '@/hooks/useCapabilitySchedule';
import { logger } from '@/utils/logger';

interface PauseScheduleModalProps {
  schedule: CapabilitySchedule | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (scheduleId: string) => Promise<void>;
}

/**
 * PauseScheduleModal - Confirmation modal for schedule pause
 *
 * Displays a confirmation message for pausing a capability schedule.
 * Unlike delete, pause is non-destructive and can be resumed later.
 */
export function PauseScheduleModal({
  schedule,
  isOpen,
  onClose,
  onConfirm,
}: PauseScheduleModalProps) {
  const [isPausing, setIsPausing] = useState(false);

  const handlePause = async () => {
    if (!schedule) return;
    setIsPausing(true);
    try {
      await onConfirm(schedule.scheduleId);
      onClose();
    } catch (error) {
      logger.error('Failed to pause schedule:', error);
      toast.error('Failed to pause schedule');
    } finally {
      setIsPausing(false);
    }
  };

  if (!schedule) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Pause Schedule"
      size="lg"
      footer={{
        text: isPausing ? 'Pausing...' : 'Pause',
        onClick: handlePause,
        disabled: isPausing,
        styleType: 'primary',
      }}
    >
      <div className="flex flex-col gap-4">
        <div className="bg-chariot-background-secondary border-chariot-stroke flex items-start gap-3 rounded-md border p-4">
          <div>
            <p className="text-chariot-text-primary mb-1 text-sm font-medium">
              Pausing this schedule will stop scheduled executions
            </p>
            <p className="text-chariot-text-secondary text-sm">
              You can resume the schedule at any time to restart automated
              executions.
            </p>
          </div>
        </div>
        <div>
          <p className="text-chariot-text-primary text-sm">
            Are you sure you want to pause the schedule for{' '}
            <b>{schedule.capabilityName}</b>?
          </p>
        </div>
      </div>
    </Modal>
  );
}
